import { useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AUTH_ACTIVITY, authActivityDetail } from '@agrogruda/shared';
import { firstIssueMessage, loginForm } from '@agrogruda/shared/schemas';
import authLogo from '@/assets/auth-logo.png';
import { redirectTarget } from '@/auth/RequireAuth';
import { useAuth } from '@/auth/useAuth';
import { useSetupStatus } from '@/auth/useSetupStatus';
import { useToast } from '@/components/ui/Toast';
import { sq } from '@/i18n/sq';
import { logActivity } from '@/lib/activity';
import { cx } from '@/lib/cx';

/**
 * React 18 does not know the camelCase `fetchPriority` prop (added in React 19) and warns about it in dev
 * while still emitting the lowercase attribute. Passing the DOM attribute directly keeps the LCP hint and
 * the console clean; drop this once React 19 lands. See docs/BUGLOG.md B27.
 */
const HIGH_FETCH_PRIORITY: Record<string, string> = { fetchpriority: 'high' };

/**
 * Prototype #authScreen (lines 886-897) + authLogin()/finishAuthLogin() (lines 3098-3104).
 * Enter submits (native form), the single error line toggles .show, success logs 'Hyrje në ERP' and toasts.
 */
export function LoginPage() {
  const { status, signIn } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const setup = useSetupStatus(status === 'anonymous');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const busy = useRef(false);

  if (status === 'authenticated') return <Navigate to={redirectTarget(location.state)} replace />;
  if (status === 'loading' || setup.isPending) return <div className="auth-screen" id="authScreen" />;
  if (setup.data?.setupRequired) return <Navigate to="/setup" replace />;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    // Prototype authLogin(): an empty username/password can never match a login → the same generic error.
    const parsed = loginForm.safeParse({ username, password });
    if (!parsed.success) {
      setError(firstIssueMessage(parsed.error));
      return;
    }
    busy.current = true;
    try {
      const profile = await signIn(parsed.data.username, parsed.data.password);
      setError('');
      void logActivity(profile, 'auth', AUTH_ACTIVITY.login, authActivityDetail(profile.name, profile.role));
      toast(sq.auth.welcome(profile.name));
      void navigate(redirectTarget(location.state), { replace: true });
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : sq.auth.invalidCredentials);
    } finally {
      busy.current = false;
    }
  };

  return (
    <div className="auth-screen" id="authScreen">
      <div className="auth-card auth-card-clean">
        <img
          className="auth-logo auth-logo-new"
          id="authLogo"
          alt={sq.app.logoAlt}
          src={authLogo}
          width={1001}
          height={445}
          {...HIGH_FETCH_PRIORITY}
        />
        <div className={cx('auth-error', error && 'show')} id="authError" role="alert">
          {error}
        </div>
        <form className="auth-form" id="loginForm" onSubmit={(e) => void submit(e)} noValidate>
          <div className="form-group">
            <label htmlFor="loginUsername">{sq.auth.username}</label>
            <input
              className="input"
              id="loginUsername"
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="loginPassword">{sq.auth.password}</label>
            <input
              className="input"
              id="loginPassword"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder={sq.auth.passwordPlaceholder}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="auth-actions">
            <button className="btn primary large" id="loginSubmitBtn" type="submit">
              {sq.auth.login}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
